/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {GenericObject} from '@ion/core/src/types/entities';

// available field names for the edit driver scorecard form
export enum DriverScoreField {
  driverScoring = 'driverScoring',
  harshAcceleration = 'harshAcceleration',
  harshDeceleration = 'harshDeceleration',
  hardCornering = 'hardCornering',
  speedingOneToFour = 'speedingOneToFour',
  speedingFiveToNine = 'speedingFiveToNine',
  speedingTenToFourteen = 'speedingTenToFourteen',
  speedingFifteenPlus = 'speedingFifteenPlus',
  nightTimeDriving = 'nightTimeDriving',
  visionCollisionWarning = 'visionCollisionWarning',
  visionDistractedDriving = 'visionDistractedDriving',
  visionTrafficViolation = 'visionTrafficViolation',
  visionLaneDrift = 'visionLaneDrift',
  visionLaneDeparture = 'visionLaneDeparture'
};

type ScoreObject = {
  [key: string]: number;
}

export const drivingCategories = {
  [DriverScoreField.harshAcceleration] : {
    value : 0
  },
  [DriverScoreField.harshDeceleration] : {
    value : 0
  },
  [DriverScoreField.hardCornering] : {
    value : 0
  },
  [DriverScoreField.nightTimeDriving] : {
    value : 0
  }
};

export const speedingCategories = {
  [DriverScoreField.speedingOneToFour] : {
    value : 0
  },
  [DriverScoreField.speedingFiveToNine] : {
    value : 0
  },
  [DriverScoreField.speedingTenToFourteen] : {
    value : 0
  },
  [DriverScoreField.speedingFifteenPlus] : {
    value : 0
  }
};

export const visionCategories = {
  [DriverScoreField.visionCollisionWarning] : {
    value : 0
  },
  [DriverScoreField.visionDistractedDriving] : {
    value : 0
  },
  [DriverScoreField.visionTrafficViolation] : {
    value : 0
  },
  [DriverScoreField.visionLaneDrift] : {
    value : 0
  }
};
/**
 * This function takes a driver score and deserializes it into
 * a driver scorecard form consumable object that can be pre-populated
 * into the form. Essentially, it's the reverse of the serialize
 * function above.
 *
 * ```typescript
 *
 *  import {deserialize} from './DriverScorecardEditorUtils';
 *
 *  const deserialized = deserialize(score);
 *
 * ```
 */
export const deserialize = (score: GenericObject) => {
  const formData = {};
  Object.entries(score).forEach(([category, value]) => {
    if (category !== DriverScoreField.visionLaneDeparture) {
      formData[category] = {
        name     : category,
        value    : Number(value),
        required : false,
        error    : false,
        isVision : !!visionCategories[category]
      };
    }
  });
  return formData;
};

/**
 * This function takes a form state and serializes it into
 * a driver scorecard(in extended attribute_ form consumable
 * object that can be populated into the CTC.
 *
 * ```typescript
 *
 *  import {serialize} from './DriverScorecardEditorUtils';
 *
 *  const serialize = serialize(formState);
 *
 * ```
 */
export const serialize = (formData: GenericObject) => {
  const serialized = {};
  Object.entries(formData).forEach(([key, props]) => {
    serialized[key] = Number(props.value);
  });
  return {[DriverScoreField.driverScoring]: serialized};
};

/**
 * This function returns the total driver score based on
 * formState objects.
 *
 * ```typescript
 *
 *  import {getTotalScore} from './DriverScorecardEditorUtils';
 *
 *  const total = getTotalScore(formState);
 *
 * ```
 */
export const getTotalScore = (formData: GenericObject, expectedTotal = 100) => {
  let error = false;
  const total =  Object.entries(formData).reduce((total, [_, props]) => {
    error ||= props.error;
    return (total + Number(props.value));
  }, 0);
  return {total, totalError: expectedTotal !== total || error};
};

/**
 * This function return true  based on the condition that if the score
 * if greater or equal to zero AND lesser or  equla to 100. Else return zero.
 *
 * ```typescript
 *
 *  import {getTotalScore} from './DriverScorecardEditorUtils';
 *
 *  const isValidScore = validateScore(score);
 *
 * ```
 */
export const validateScore = (score: number) => score < 0 || score > 100;

/**
 * This function checks whether any value is missing for a vision category and returns true if it's saved,
 * otherwise returns false.
 * @param {object} score - An object containing the scores for different vision categories.
 * @returns {boolean} - Returns true if all values for a vision category are present, otherwise returns false.
 *
 * ```typescript
 * import { isVisionCategorySaved } from './DriverScorecardEditorUtils';
 * const isSaved = isVisionCategorySaved(score);
 * ```
 */
export const isVisionCategorySaved = (score: ScoreObject): boolean => {
  for (const [key, value] of Object.entries(score)) {
    if (visionCategories[key] && value !== 0) {
      return true;
    }
  }
  return false;
};
